import moment from "moment";
import DateRangePickerComponent from "../../../components/common/CustomDateRangePicker";
import {Box, Button, CircularProgress, FormControl, Grid, InputAdornment, MenuItem, Pagination, Paper, Select, Table, TableBody, TableCell, TableContainer, TableRow, TextField, Typography } from "@mui/material";
import DownloadIcon from '@mui/icons-material/Download';
import React, { useEffect, useState } from "react";
import ReportsTableHeader from "../report-table-header.component";
import ProductVerificationSkeleton from "../skeleton/product-verification-skeleton";
import ReportService from "../../../services/api/reports";
import ExportAlertNotificationDialog from "../../alert/export-alert-notification-dialog.component";
import ReactSelect from "react-select";
import Tooltip, {tooltipClasses} from "@mui/material/Tooltip";
import {styled} from "@mui/system";
import CachedIcon from "@mui/icons-material/Cached";
import {useSnackbar} from "notistack";

const LightTooltip = styled(({ className, ...props }) => (
    <Tooltip {...props} classes={{popper: className}} />

    ))(({theme}) => ({
        [`& .${tooltipClasses.tooltip}`]: {
            backgroundColor: "#fff",
            color: "rgba(51, 51, 207, 0.808)",
            boxShadow: "0px 5px 5px -3px rgb(0 0 0 / 10%), 0px 8px 10px 1px rgb(0 0 0 / 4%), 0px 3px 14px 2px rgba(0,0,0,0.12)",
            fontSize: 13,
            padding: theme.spacing(2),
            borderRadius: theme.shape.borderRadius, 
        },
  
        "& .MuiTooltip-arrow": {
            color: "#fff", 

            "&:before": {
                //
            }
        }
    })
);

const SmallSelect = styled(Select)(({ theme, fontColor }) => ({
    minWidth: 120,  
    height: 33,     
    fontSize: '0.875rem',  
    '.MuiSvgIcon-root': {
        color: fontColor, 
    },
    color: fontColor,
    backgroundColor: '#fff',
    border: "1px solid #7A78F7",
    "& .MuiInputBase-root": { height: 33, border: "1px solid #7A78F7", color: "#3333CFCE", backgroundColor: '#fff', width: 250, borderRadius: "5px" },
    "& .MuiOutlinedInput-notchedOutline": {
        border: "none",
    },
    "&:hover .MuiOutlinedInput-notchedOutline": {
        border: "none",
    },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
        border: "none",
    },
    "& .MuiSelect-select": {
        fontSize: 12,
        fontStyle: 'italic'
    },
    "& .MuiMenuItem-root": {
        fontSize: 12,
    },
    "& .Mui-selected": {
        fontSize: 12,
        fontStyle: 'italic'
    },
}));

const filterStyles = {
    control: (styles, { isFocused }) => ({
        ...styles,
        textAlign: "left",
        backgroundColor: "#fff",
        minHeight: 20, 
        padding: "0 8px", 
        height: 34, 
        width: 280,
        fontSize: 14,
        borderColor: "rgb(122, 120, 247)",
        '&:hover': {
            borderColor: "rgb(122, 120, 247)", 
        },
        zIndex: 1200,
    }),
    menu: (provided) => ({
        ...provided,
        zIndex: 1200,
    }),
    menuPortal: (provided) => ({
        ...provided,
        zIndex: 9999, 
    }),
    valueContainer: (styles) => ({
        ...styles,
        padding: "0px 6px",
    }),
    input: (styles) => ({
        ...styles,
        margin: 0,
        padding: 0, 
    }),
    option: (styles) => ({...styles, textAlign: "left", fontSize: 13}),
    dropdownIndicator: (styles) => ({
        ...styles,
        color: "rgb(122, 120, 247)", 
        marginTop: "-2px",
        '&:hover': {
            color: "rgb(122, 120, 247)",
        },
    }),
    clearIndicator: (styles) => ({
        ...styles,
        color: "rgb(122, 120, 247)", 
        marginTop: "-2px",
        '&:hover': {
            color: "rgb(122, 120, 247)", 
        },
    }),
    singleValue: (styles) => ({
        ...styles,
        color: "rgba(51, 51, 207, 0.808)",
        marginTop: "0px",
        fontSize: 13,
    }),
    placeholder: (styles) => ({ ...styles, color: "#3333CFCE", marginTop: "0px", fontSize: 12}), 
    noOptionsMessage: (provided) => ({
        ...provided,
        fontStyle: "italic", 
        textAlign: "start", 
        fontSize: "13px", 
    }),
}


const ProductSpecifiVerificationFilterComponent = (props) => {

    const {headers, getProductVerification, isLoadingProductVerification, listProductVerification, getCityMunicipality, 
        listProducts, page, setPage, listOfCountry, getRegion, regionList, getProvince, listOfProvince, listOfCityMuni,
    } = props;

    const {enqueueSnackbar} = useSnackbar();

        const [region_name, setRegion] = useState(null);
        const [country_name, setCountry] = useState(null);
        const [province_name, setProvince] = useState(null);
        const [cityMuni, setCityMuni] = useState(null);
        const [product_name, setProductName] = useState("");
        const [open, setOpen] = useState(false)
        const [isLoadingAlertNotification, setIsLoadingAlertNotification] = useState(false)
        const [reportID, setReportID] = useState(null)

        const [formattedProductList, setFormattedProductList] = useState([]);
        const [startDate, setStartDate] = useState(moment().subtract(6, 'days').format('YYYY-MM-DD'));
        const [endDate, setEndDate] = useState(moment().format('YYYY-MM-DD'));

        useEffect(() => {
            if (listProducts) {
                const formattedList = listProducts?.map(product => ({
                    label: product.product_name,
                    value: product.product_code
                }));

                setFormattedProductList(formattedList);
            }
        }, [listProducts])

        useEffect(() => {
            const start_date = startDate;
            const end_date = endDate;
            const product_code = product_name?.value || '';
            const country = country_name || '';
            const region = region_name || '';
            const province = province_name || '';
            const city_municipality = cityMuni || '';
        
            let report_type;
        
            if (!product_code && !country && !region && !province && !city_municipality) {
                report_type = 1; // Default to 1 (startDate + endDate)
            } else if (product_code) {
                if (country) {
                    if (region) {
                        if (province) {
                            report_type = city_municipality ? 10 : 9; // Based on city_municipality presence
                        } else {
                            report_type = 8; // startDate + endDate + country + region + product
                        }
                    } else {
                        report_type = 7; // startDate + endDate + country + product
                    }
                } else {
                    report_type = 2; // startDate + endDate + product
                }
            } else if (country) {
                if (region) {
                    if (province) {
                        report_type = city_municipality ? 6 : 5; // Based on city_municipality presence
                    } else {
                        report_type = 4; // startDate + endDate + country + region
                    }
                } else {
                    report_type = 3; // startDate + endDate + country
                }
            }
        
            const payload = {
                start_date: start_date,
                end_date: end_date,
                product_code: product_code ? [product_name?.value] : [],
                country: country,
                region: region,
                province: province,
                city_municipality: city_municipality,
                report_type: report_type
            };
        
            getProductVerification(payload);
        }, [startDate, endDate, product_name, country_name, region_name, province_name, cityMuni]);
        

        const handleSelectProductFilter = (selectedOption) => {
            if (!selectedOption) {
                setProductName("");
    
            } else {
                setProductName(selectedOption);
            }
        }

        const handleClose = () =>{
            setOpen(false)
        }
    
        const handleDateRangeChange = (newStartDate, newEndDate) => {
            setStartDate(newStartDate);
            setEndDate(newEndDate);
        }
    
        const handleSelectCountry = (event) => {
            setCountry(event.target.value);
            setRegion(null);
            setProvince(null);
            setCityMuni(null);
        }
    
        const handleSelectRegion = (event) => {
            setRegion(event.target.value);
            setProvince(null);
            setCityMuni(null);
        }
    
        const handleSelectProvince = (event) => {
            setProvince(event.target.value);
            setCityMuni(null);
        }
    
        const handleSelectCityMuni = (event) => {
            setCityMuni(event.target.value)
        }
    
        const itemsPerPage = 100;
    
        // Calculate the total number of pages
        const totalPages = Math.ceil((listProductVerification?.length || 0) / itemsPerPage);
    
        // Get the current items to display
        const currentItems = (listProductVerification || []).slice((page - 1) * itemsPerPage, page * itemsPerPage);
    
        // Handler to change page
        const handlePageChange = (event, value) => {
            setPage(value);
        };
    
        
        
        const handleExport = () => {
            const start_date = startDate;
            const end_date = endDate;
            const product_code = product_name?.value || '';
            const country = country_name || '';
            const region = region_name || '';
            const province = province_name || '';
            const city_municipality = cityMuni || '';
        
            let report_type;
        
            if (!product_code && !country && !region && !province && !city_municipality) {
                report_type = 1; // Default to 1 (startDate + endDate)
            } else if (product_code) {
                if (country) {
                    if (region) {
                        if (province) {
                            report_type = city_municipality ? 10 : 9; // Based on city_municipality presence
                        } else {
                            report_type = 8; // startDate + endDate + country + region + product
                        }
                    } else {
                        report_type = 7; // startDate + endDate + country + product
                    }
                } else {
                    report_type = 2; // startDate + endDate + product
                }
            } else if (country) {
                if (region) {
                    if (province) {
                        report_type = city_municipality ? 6 : 5; // Based on city_municipality presence
                    } else {
                        report_type = 4; // startDate + endDate + country + region
                    }
                } else {
                    report_type = 3; // startDate + endDate + country
                }
            }
        
            const payload = {
                start_date: start_date,
                end_date: end_date,
                product_code: product_code ? [product_name?.value] : [],
                country: country,
                region: region,
                province: province,
                city_municipality: city_municipality,
                report_type: report_type
            };

            if (listProductVerification?.length > 0) {
                setIsLoadingAlertNotification(true);

                ReportService.exportProductVerification(payload).then((res)=>{
                    console.log('get export alert and notification res here', res)
                    setIsLoadingAlertNotification(false)
                    setReportID(res?.data?.data?.report_id)
                    setOpen(true)
                }).catch(()=>{
                    setIsLoadingAlertNotification(false);
                })

            } else {
                enqueueSnackbar("No data to export", { 
                    variant: "error",
                    autoHideDuration: 3000
                });
            }
            
        }
    
        useEffect(() => {
            if (country_name) {
                getRegion(country_name);
        
                if (region_name) {
                    getProvince(country_name, region_name);
        
                    if (province_name) {
                        getCityMunicipality(country_name, region_name, province_name);
                    }
                }
            }
        }, [country_name, region_name, province_name, page]);


    const handleClearFilters = (e) => {
        setProductName("");
        setCountry();
        setRegion();
        setProvince();
        setCityMuni();
        setStartDate(moment().subtract(6, "days").format("YYYY-MM-DD"));
        setEndDate(moment().format("YYYY-MM-DD"));
    }

      
    return (
        <Box>
            <Grid container sx={{justifyContent: "flex-end", alignItems: "center", mt: 3}}>
                <Grid item xs={3}>
                    <Button variant="contained" sx={{height: "33px", textTransform: "none", backgroundColor: "#5959D8", color: "#fff", "&:hover": {backgroundColor: "#5959D8", color: "#fff"}}}
                        startIcon={<CachedIcon />} onClick={(e) => handleClearFilters(e)}
                    >
                        Clear Filters
                    </Button>
                </Grid>

                <Grid item xs={9} sx={{ display: 'flex', justifyContent: "flex-end" }}>
                    <ReactSelect
                        isRtl={false}
                        options={formattedProductList}
                        value={product_name}
                        onChange={selectedOption => handleSelectProductFilter(selectedOption)}
                        styles={filterStyles}
                        isClearable={true}
                        placeholder={(product_name) ? null : "Select Product Name"}
                        noOptionsMessage={() => "No matching product found."} 
                        menuPortalTarget={document.body} 
                        menuPosition="fixed"
                    /> 
    
                    &nbsp;&nbsp;&nbsp;&nbsp;

                    <FormControl sx={{ width: 250 }}>
                        <SmallSelect
                            value={country_name}
                            onChange={handleSelectCountry}
                            displayEmpty
                            fontColor={'#5d5ed8'}
                            startAdornment={
                                <InputAdornment position="start">
                                    <p style={{ fontSize: 12, margin: 0, color: "#3333CFCE", fontWeight: 'bold' }}>
                                        Country:
                                    </p>
                                </InputAdornment>
                            }
                        >
                            <MenuItem value="" disabled sx={{ fontSize: 12 }}>
                                <em>Select Country</em>
                            </MenuItem>
                           
                            {listOfCountry?.map((list, index)=>{
                                return(
                                    <MenuItem value={list?.country} sx={{ fontSize: 12 }}>
                                        {list?.country}
                                    </MenuItem>
                                )
                            })}
                        </SmallSelect>
                    </FormControl>

                    &nbsp;&nbsp;&nbsp;&nbsp;
                    
                    <DateRangePickerComponent
                        initialStartDate={startDate}
                        initialEndDate={endDate}
                        ranges={{
                            "Last 7 Days": [moment().subtract(6, 'days'), moment()],
                            "This Month": [moment().startOf('month'), moment().endOf('month')],
                            "Last Month": [moment().subtract(1, 'month').startOf('month'), moment().subtract(1, 'month').endOf('month')],
                            "Last 90 Days": [moment().subtract(89, 'days'), moment()],
                        }}
                        onDateRangeChange={handleDateRangeChange}
                    />
                    &nbsp;&nbsp;&nbsp;&nbsp;
                    <Button
                        onClick={handleExport}
                        variant="contained"
                        startIcon= {isLoadingAlertNotification ? <CircularProgress sx={{color:'white'}} size={11} /> : <DownloadIcon />}
                        sx={{ fontSize: 12, background: '#3333CFCE', color: '#fff', textTransform: 'none', "&:hover": {backgroundColor: "#5959D8", color: "#fff"}}}
                    >
                        {isLoadingAlertNotification ? "Exporting" : "Export"}
                    </Button>
                </Grid>

                
                <Grid item xs={12} sx={{ display: 'flex', justifyContent: "flex-end", mt: 2 }}>
                    {country_name &&
                        <FormControl sx={{ width: 250 }}>
                            <SmallSelect
                                value={region_name}
                                onChange={handleSelectRegion}
                                displayEmpty
                                fontColor={'#5d5ed8'}
                                startAdornment={
                                    <InputAdornment position="start">
                                        <p style={{ fontSize: 12, margin: 0, color: "#3333CFCE", fontWeight: 'bold' }}>
                                            Region:
                                        </p>
                                    </InputAdornment>
                                }
                            >
                                <MenuItem value="" disabled sx={{ fontSize: 12 }}>
                                    <em>Select Region</em>
                                </MenuItem>
                            
                                {regionList?.map((list, index)=>{
                                    return(
                                        <MenuItem value={list?.region} sx={{ fontSize: 12 }}>
                                            {list?.region}
                                        </MenuItem>
                                    )
                                })}
                            </SmallSelect>
                        </FormControl>
                    }

                    {region_name && 
                        <>
                            &nbsp;&nbsp;&nbsp;&nbsp;

                            <FormControl sx={{ width: 250 }}>
                                <SmallSelect
                                    value={province_name}
                                    onChange={handleSelectProvince}
                                    displayEmpty
                                    fontColor={'#5d5ed8'}
                                    startAdornment={
                                        <InputAdornment position="start">
                                            <p style={{ fontSize: 12, margin: 0, color: "#3333CFCE", fontWeight: 'bold' }}>
                                                Province:
                                            </p>
                                        </InputAdornment>
                                    }
                                >
                                    <MenuItem value="" disabled sx={{ fontSize: 12 }}>
                                        <em>Select Province</em>
                                    </MenuItem>
                                
                                    {listOfProvince?.map((list, index)=>{
                                        return(
                                            <MenuItem value={list?.province} sx={{ fontSize: 12 }}>
                                                {list?.province}
                                            </MenuItem>
                                        )
                                    })}
                                </SmallSelect>
                            </FormControl>
                        </>
                    }

                    {province_name &&
                        <>
                            &nbsp;&nbsp;&nbsp;&nbsp;

                            <FormControl sx={{ width: 250 }}>
                                <SmallSelect
                                    value={cityMuni}
                                    onChange={handleSelectCityMuni}
                                    displayEmpty
                                    fontColor={'#5d5ed8'}
                                    startAdornment={
                                        <InputAdornment position="start">
                                            <p style={{ fontSize: 12, margin: 0, color: "#3333CFCE", fontWeight: 'bold' }}>
                                                City/Municipality:
                                            </p>
                                        </InputAdornment>
                                    }
                                >
                                    <MenuItem value="" disabled sx={{ fontSize: 12 }}>
                                        <em>Select City/Municipality</em>
                                    </MenuItem>
                                
                                    {listOfCityMuni?.map((list, index)=>{
                                        return(
                                            <MenuItem value={list?.city} sx={{ fontSize: 12 }}>
                                                {list?.city}
                                            </MenuItem>
                                        )
                                    })}
                                </SmallSelect>
                            </FormControl>
                        </>
                    }
                </Grid>
            </Grid>

            <Grid item xs={12}>
                <Paper sx={{ width: '100%', overflow: 'hidden', mt: 3,  }}>
                    <TableContainer sx={{ border: "1px solid #3333cf54", borderRadius: 2, width: "100%", maxHeight: 600}}>
                        <Table stickyHeader sx={{ minWidth: 650 }}>
                            <ReportsTableHeader headers={headers} />
                            {isLoadingProductVerification ?
                                <TableBody>
                                    <ProductVerificationSkeleton/>
                                </TableBody>
                            :
                                <TableBody>
                                    {currentItems?.map((label, index) => (
                                        <LightTooltip
                                            key={index}
                                            title={
                                                <React.Fragment>
                                                    <Typography variant="body2" color="inherit" sx={{mb: "5px"}}>
                                                        <strong>Product:</strong> {label?._id.productInfo.product_name || "N / A"}
                                                    </Typography>

                                                    <Typography variant="body2" color="inherit" sx={{mb: "5px"}}>
                                                        <strong>Success Percentage: </strong>  
                                                        {Number.isInteger(label?.success_percentage)? 
                                                            `${label?.success_percentage}%`
                                                        : 
                                                            `${Number(label?.success_percentage)?.toFixed(2)}%`
                                                        }
                                                    </Typography>

                                                    <Typography variant="body2" color="inherit" sx={{mb: "5px"}}>
                                                        <strong>Invalid Percentage: </strong> 
                                                        {Number.isInteger(label?.invalid_percentage)? 
                                                            `${label?.invalid_percentage}%`
                                                        : 
                                                            `${Number(label?.invalid_percentage)?.toFixed(2)}%`
                                                        }
                                                    </Typography>

                                                    <Typography variant="body2" color="inherit">
                                                        <strong>Suspicious Percentage: </strong> 
                                                        {Number.isInteger(label?.suspicious_percentage)? 
                                                            `${label?.suspicious_percentage}%`
                                                        : 
                                                            `${Number(label?.suspicious_percentage)?.toFixed(2)}%`
                                                        }
                                                    </Typography>
                                                </React.Fragment>
                                            }
                                            arrow
                                            placement="bottom-start"
                                        >
                                            <TableRow key={index} sx={{ '&:last-child td, &:last-child th': { border: 0 }, cursor: "pointer"}}>
                                                <TableCell component="th" scope="row">
                                                    {label._id?.product_code}
                                                </TableCell>

                                                <TableCell component="th" scope="row">
                                                    {label?._id.productInfo.product_name}
                                                </TableCell>

                                                <TableCell>
                                                    {label?.coordinates_success?.length === 0 ? 
                                                        "N / A" 
                                                    : 
                                                        (label?.coordinates_success.map((coordinate, index) => (
                                                            <Typography variant="body2" key={index} style={{ marginBottom: 4 }}>
                                                                {'Latitude ' + coordinate?.latitude + ', '} {'Longitude ' + coordinate?.longitude}
                                                            </Typography>
                                                        )))
                                                    }
                                                </TableCell>

                                                <TableCell component="th" scope="row">
                                                    {label?._id.region}
                                                </TableCell>

                                                <TableCell component="th" scope="row">
                                                    {label?._id.country}
                                                </TableCell>
                                                
                                                <TableCell align="center">
                                                    {label?.success_count ? label.success_count : 0}
                                                </TableCell>

                                                <TableCell align="center">
                                                    {label?.invalid_count ? label.invalid_count : 0}
                                                </TableCell>

                                                <TableCell align="center">
                                                    {label?.suspicious_count ? label.suspicious_count : 0}
                                                </TableCell>
                                            </TableRow>
                                        </LightTooltip>
                                    ))}
                                </TableBody>
                            }
                        </Table>

                        {/* ERROR MESSAGE */}
                        {!isLoadingProductVerification && currentItems?.length === 0 && <Box sx={{textAlign:'center', m: 2}}> <Typography sx={{color:'gray', fontSize: 12}}>No data available.</Typography></Box>}

                    </TableContainer>
                   
                    {!isLoadingProductVerification ?
                            <Box display="flex" justifyContent="center" mt={2} mb={2}>
                                <Pagination
                                    count={totalPages}
                                    page={page}
                                    onChange={handlePageChange}
                                    sx={{
                                        "& .MuiPaginationItem-root.Mui-selected": {
                                            color: "#fff",
                                            backgroundColor: "#5959D8"
                                        }
                                    }} 
                                />
                            </Box>
                    : null}
                </Paper>
            </Grid>
          
            <ExportAlertNotificationDialog open={open} reportID={reportID} handleClose={handleClose}/>
        </Box>
    )

}

export default ProductSpecifiVerificationFilterComponent